"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { TrendingDown, BarChart3 } from "lucide-react";

const ITEMS = [
  { name: "Beef chuck", theoretical: 18.2, actual: 18.5, unit: "lb", variance: -0.3 },
  { name: "Tequila Blanco", theoretical: 84, actual: 78, unit: "oz", variance: 6, big: true },
  { name: "Corn tortillas", theoretical: 412, actual: 408, unit: "pcs", variance: 4 },
  { name: "Lime", theoretical: 24, actual: 22, unit: "lb", variance: 2 },
];

export default function InventoryVarianceScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Variance reports", "Reportes de varianza")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Theoretical vs actual. ", "Teórico vs real. ")}
            <span className="text-redy-charcoal/30">{t("Find the leak.", "Encuentra la fuga.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Theoretical usage (recipes × orders sold) compared against actual usage (your end-of-shift count). When tequila is 6 oz short of where the recipes say it should be, you have a heavy-pouring bartender — not a mystery.",
              "Uso teórico (recetas × órdenes vendidas) comparado contra uso real (tu conteo al cerrar). Cuando el tequila está 6 oz por debajo de donde dicen las recetas, tienes un bartender que pour-ea pesado — no es misterio."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-[#FAFAF8] border border-black/5 rounded-[28px] p-7 md:p-8 max-w-[820px] mx-auto"
        >
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                {t("Variance report · Week of Sep 8", "Reporte de varianza · Semana del 8 Sep")}
              </div>
              <div className="text-[16px] font-black text-redy-charcoal">
                {t("Theoretical vs actual usage", "Uso teórico vs real")}
              </div>
            </div>
            <BarChart3 className="w-4 h-4" style={{ color: "#F4722B" }} />
          </div>

          <div className="space-y-4">
            {ITEMS.map((item, i) => {
              const pct = (Math.abs(item.variance) / item.theoretical) * 100;
              return (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: 0.2 + 0.12 * i }}
                  className={`rounded-[14px] p-4 border ${
                    item.big ? "" : "bg-white border-black/5"
                  }`}
                  style={
                    item.big
                      ? {
                          backgroundColor: "rgba(220,38,38,0.06)",
                          borderColor: "rgba(220,38,38,0.30)",
                        }
                      : undefined
                  }
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-[14px] font-black text-redy-charcoal">{item.name}</div>
                    <div
                      className="text-[13px] font-black tabular-nums"
                      style={{ color: item.big ? "#DC2626" : "#F4722B" }}
                    >
                      −{item.variance} {item.unit}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3 text-[11px]">
                    <div>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/55 mb-0.5">
                        {t("Theoretical (recipes)", "Teórico (recetas)")}
                      </div>
                      <div className="text-[12px] font-bold text-redy-secondary tabular-nums">
                        {item.theoretical} {item.unit}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/55 mb-0.5">
                        {t("Actual (counted)", "Real (contado)")}
                      </div>
                      <div className="text-[12px] font-bold text-redy-charcoal tabular-nums">
                        {item.actual} {item.unit}
                      </div>
                    </div>
                  </div>

                  {/* Variance bar */}
                  <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: "0%" }}
                      whileInView={{ width: `${Math.min(pct * 4, 100)}%` }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 1.0, delay: 0.4 + 0.12 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                      className="h-full rounded-full"
                      style={{
                        background: item.big
                          ? "linear-gradient(90deg, #F4722B 0%, #DC2626 100%)"
                          : "linear-gradient(90deg, #F4722B 0%, #FCA17D 100%)",
                      }}
                    />
                  </div>

                  {item.big && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.5, delay: 0.8 + 0.12 * i }}
                      className="mt-3 flex items-center gap-2 text-[11px]"
                      style={{ color: "#DC2626" }}
                    >
                      <TrendingDown className="w-3 h-3" />
                      <span className="font-bold">
                        {t("Likely heavy-pour or shrinkage. Investigate.", "Probablemente pour pesado o merma. Investiga.")}
                      </span>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="mt-6 pt-5 border-t border-black/5 flex items-center justify-between"
          >
            <div className="text-[12px] font-bold text-redy-charcoal">
              {t("Total $ variance this week", "Total $ varianza esta semana")}
            </div>
            <div
              className="text-[20px] font-black tabular-nums"
              style={{
                background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              −$87.40
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
